import React from 'react';
import { Alert, Tag, Typography } from 'antd';
import { cardMeta } from './cardTypes';
import { ItemActions, SchemaFields } from './fields';
import { Card } from './types';
import { CardEditorProps } from './editors/types';
import ScenarioEditor from './editors/ScenarioEditor';
import ChatSimulatorEditor from './editors/ChatSimulatorEditor';
import StoryModeEditor from './editors/StoryModeEditor';
import DragRankEditor from './editors/DragRankEditor';
import StepChatEditor from './editors/StepChatEditor';

const { Text } = Typography;

/** Editors for the parts of a card its field schema can't express. */
const CUSTOM_EDITORS: Partial<Record<string, React.FC<CardEditorProps>>> = {
  scenario: ScenarioEditor,
  chat_simulator: ChatSimulatorEditor,
  story_mode: StoryModeEditor,
  drag_rank: DragRankEditor,
  step_chat: StepChatEditor,
};

const MAX_LISTED_ISSUES = 6;

interface Props extends CardEditorProps {
  index: number;
  total: number;
  issues: string[];
  onMove: (to: number) => void;
  onDuplicate: () => void;
  onDelete: () => void;
}

const CardEditor: React.FC<Props> = ({ card, onChange, index, total, issues, onMove, onDuplicate, onDelete }) => {
  const meta = cardMeta(card.type);
  if (!meta) {
    return <Alert type="error" showIcon title={`Unknown card type “${card.type}”`} description="Switch to JSON to edit this step." />;
  }
  const Custom = CUSTOM_EDITORS[card.type];
  const setFields = (next: Record<string, any>) => onChange(next as Card);
  const hidden = issues.length - MAX_LISTED_ISSUES;

  return (
    <div>
      <div className="lbe-detail-head">
        <span style={{ display: 'inline-flex', alignItems: 'center', gap: 8 }}>
          <Tag color={meta.group === 'test' ? 'purple' : 'blue'} style={{ marginInlineEnd: 0 }}>{meta.label}</Tag>
          <Text type="secondary">Step {index + 1} of {total}</Text>
        </span>
        <ItemActions
          index={index}
          count={total}
          onMove={onMove}
          onDuplicate={onDuplicate}
          onRemove={onDelete}
          confirmRemove="Delete this step?"
        />
      </div>
      <Text type="secondary" style={{ fontSize: 12 }}>{meta.description}</Text>

      {issues.length > 0 && (
        <Alert
          style={{ marginTop: 12 }}
          type="warning"
          showIcon
          title={`${issues.length} thing${issues.length === 1 ? '' : 's'} to check`}
          description={
            <ul style={{ margin: 0, paddingLeft: 18 }}>
              {issues.slice(0, MAX_LISTED_ISSUES).map((issue, i) => <li key={i}>{issue}</li>)}
              {hidden > 0 && <li>…and {hidden} more</li>}
            </ul>
          }
        />
      )}

      <div style={{ marginTop: 16 }}>
        <SchemaFields fields={meta.fields} value={card} onChange={setFields} />
      </div>
      {Custom && <Custom card={card} onChange={onChange} />}
      {meta.fieldsAfter && (
        <div style={{ marginTop: 20 }}>
          <SchemaFields fields={meta.fieldsAfter} value={card} onChange={setFields} />
        </div>
      )}
    </div>
  );
};

export default CardEditor;
